import {
    AdvancedList,
    AdvancedListItem,
    EntityExtraVersionRef,
    SandboxFileKeyFileName
} from "@amzn/altar-sds-client";
import {
    APIOutput,
    ContainerRef,
    FieldConfiguration,
    IAdvancedList
} from "@amzn/ask-legal-domain";
import {
    Box,
    Flashbar,
    FlashbarProps,
    SpaceBetween,
    Spinner,
    SpinnerProps
} from "@amzn/awsui-components-react";
import * as React from "react";
import { AppContext } from "../../setup/context";
import {
    UpdateAdvancedListItemCommandInputEdit,
    UpdateAdvancedListItemCommandInputEditProps
} from "./UpdateAdvancedListItemCommandInputEdit";
import { Builder } from "builder-pattern";
import { UIModel } from "../../model/ui-model";
import { AttachmentUpload } from "./attachment-components/AttachmentUpload";
import { AttachmentTable } from "./attachment-components/AttachmentsTable";
import { SDSUtils } from "../../utils/sds-utils";
import { ConfirmLeavePageModal } from "../common/ConfirmLeavePageModal";
import { SimpleModal } from "../common/SimpleModal";

export interface UpdateAdvancedListItemModalProps {
    itemRef: EntityExtraVersionRef;
    advancedList: AdvancedList;
    containerRef: ContainerRef;
    fieldConfigurations?: FieldConfiguration.Record;
    onUpdated?: (item: AdvancedListItem) => void;
    onCanceled?: () => void;
}

export function UpdateAdvancedListItemModal(props: UpdateAdvancedListItemModalProps) {
    const context = React.useContext(AppContext);
    const [spinnerProps, setSpinnerProps] = React.useState<SpinnerProps>();
    const [flashbarProps, setFlashbarProps] = React.useState<Pick<FlashbarProps, "items">>();
    const [
        updateAdvancedListItemCommandInputEditProps,
        setUpdateAdvancedListItemCommandInputEditProps
    ] = React.useState<UpdateAdvancedListItemCommandInputEditProps>();
    const [sandboxFiles, setSandboxFiles] = React.useState<SandboxFileKeyFileName[]>([]);
    const [uploadingSandboxFiles, setUploadingSandboxFiles] = React.useState(false);
    const filesPendingDelete = UIModel.State.useArray<string>({ initialValue: [] });

    async function init() {
        try {
            setSpinnerProps({});

            const loadItemOutput = await context
                .getAdvancedListAPI()
                .loadItem({
                  entityId: props.itemRef.entityExtraRef.entityRef.entityId,
                  repositoryId: props.itemRef.entityExtraRef.entityRef.repositoryRef.repositoryId,
                  entityExtraId: props.itemRef.entityExtraRef.extraId,
                  containerId: props.containerRef.id,
                  by: SDSUtils.getAmazonPersonRef(context.userId)
                });

            const output = APIOutput.fromRaw<IAdvancedList.LoadAdvancedListItemOutput>(loadItemOutput.data);
            if (output.isErr()) {
                setFlashbarProps({
                    items: [
                        {
                            type: "error",
                            content: output.err.message
                        }
                    ]
                });
            } else {
                setFlashbarProps({
                    items: []
                });

                setUpdateAdvancedListItemCommandInputEditProps({
                    item: output.data.body,
                    advancedList: Builder(props.advancedList)
                        .fieldDefinitions(props.advancedList.fieldDefinitions
                        .filter((def) => !def.deprecated))
                        .build()
                });
            }
        } catch (err) {
            setFlashbarProps({
                items: [
                    {
                        type: "error",
                        content: (err as Error).message
                    }
                ]
            });
        } finally {
            setSpinnerProps(undefined);
        }
    }

    async function updateItem() {
        try {
            setSpinnerProps({});
            let outputAL: AdvancedListItem;

            if (sandboxFiles.length) {
                const uploadFilesOutput = await context
                    .getAdvancedListAPI()
                    .batchUploadItemAttachment(Builder<IAdvancedList.BatchUploadAdvancedListItemAttachmentInput>()
                        .by(SDSUtils.getAmazonPersonRef(context.userId))
                        .sandboxFiles(sandboxFiles)
                        .entityId(props.itemRef.entityExtraRef.entityRef.entityId)
                        .repositoryId(props.itemRef.entityExtraRef.entityRef.repositoryRef.repositoryId!)
                        .entityExtraId(props.itemRef.entityExtraRef.extraId)
                        .containerId(props.containerRef.id)
                        .build()
                );
                const output = APIOutput.fromRaw<IAdvancedList.BatchUploadAdvancedListItemAttachmentOutput>(uploadFilesOutput.data);
                if (output.isErr()) {
                    setFlashbarProps({
                        items: [
                            {
                                type: "error",
                                content: output.err.message
                            }
                        ]
                    });
                } else {
                    setFlashbarProps({
                        items: []
                    });
                    outputAL = output.data.body;
                }
            }

            if (filesPendingDelete.value.length) {
                const deleteAttachmentsOutput = await context
                    .getAdvancedListAPI()
                    .batchDeleteItemAttachment(
                        Builder< IAdvancedList.BatchDeleteAdvancedListItemAttachmentInput>()
                        .by(SDSUtils.getAmazonPersonRef(context.userId))
                        .keys(filesPendingDelete.value)
                        .entityId(props.itemRef.entityExtraRef.entityRef.entityId)
                        .repositoryId(props.itemRef.entityExtraRef.entityRef.repositoryRef.repositoryId!)
                        .entityExtraId(props.itemRef.entityExtraRef.extraId)
                        .build()
                );
                const output = APIOutput.fromRaw<IAdvancedList.BatchDeleteAdvancedListItemAttachmentOutput>(
                    deleteAttachmentsOutput.data
                );
                if (output.isErr()) {
                    setFlashbarProps({
                        items: [
                            {
                                type: "error",
                                content: output.err.message
                            }
                        ]
                    });
                } else {
                    setFlashbarProps({
                        items: []
                    });
                    outputAL = output.data.body;
                }
                filesPendingDelete.reset();
            }

            if (updateAdvancedListItemCommandInputEditProps?.value) {
                const updateAdvancedListItemInput: IAdvancedList.UpdateAdvancedListItemInput = {
                    ...updateAdvancedListItemCommandInputEditProps.value,
                    containerId: props.containerRef.id,
                    by: SDSUtils.getAmazonPersonRef(context.userId)
                };
                const updateItemOutput = await context
                    .getAdvancedListAPI()
                    .updateItem(updateAdvancedListItemInput);

                const output = APIOutput.fromRaw<IAdvancedList.UpdateAdvancedListItemOutput>(updateItemOutput.data);
                if (output.isErr()) {
                    setFlashbarProps({
                        items: [
                            {
                                type: "error",
                                content: output.err.message
                            }
                        ]
                    });
                } else {
                    setFlashbarProps({
                        items: []
                    });
                    outputAL = {
                        ...outputAL,
                        ...output.data.body,
                        attachments: outputAL?.attachments || output.data.body.attachments
                    };
                }
            }
            if (outputAL) {
                props.onUpdated?.(outputAL);
            }
        } catch (err) {
            setFlashbarProps({
                items: [
                    {
                        type: "error",
                        content: (err as Error).message
                    }
                ]
            });
        } finally {
            setSpinnerProps(undefined);
        }
    }

    React.useEffect(() => {
        init();
    }, [props.itemRef]);

    return (
        <>
            <ConfirmLeavePageModal showModal={true} />
            <SimpleModal
                child={
                    <SpaceBetween size="m">
                        {spinnerProps && <Box textAlign="center">
                            <SpaceBetween size="s" direction="horizontal">
                                <Spinner />
                                <span>Loading...</span>
                            </SpaceBetween>
                        </Box>}
                        {(flashbarProps?.items.length ?? 0) > 0 && <Flashbar {...flashbarProps} />}
                        {updateAdvancedListItemCommandInputEditProps && (
                            <SpaceBetween size="m">
                                <UpdateAdvancedListItemCommandInputEdit
                                    {...updateAdvancedListItemCommandInputEditProps}
                                    fieldConfigurations={props.fieldConfigurations}
                                    onChanged={(value) => {
                                        setUpdateAdvancedListItemCommandInputEditProps((prev) => ({
                                            ...prev,
                                            value: value
                                        }));
                                    }}
                                />
                                <hr />
                                <AttachmentUpload
                                    itemRef={props.itemRef}
                                    onUpdated={(files) => {
                                        setSandboxFiles(files);
                                    }}
                                    setUploadingSandboxFiles={setUploadingSandboxFiles}
                                />
                                <AttachmentTable
                                    item={updateAdvancedListItemCommandInputEditProps?.item}
                                    itemRef={props.itemRef}
                                    editing
                                    filesPendingDelete={filesPendingDelete}
                                />
                            </SpaceBetween>
                        )}
                    </SpaceBetween>
                }
                header="Update Item"
                loading={!!spinnerProps || uploadingSandboxFiles}
                onCancel={props.onCanceled}
                onSave={updateItem}
                disabled={uploadingSandboxFiles}
                size="large"
            />
        </>
    );
}
